/**
 * Composable pour la gestion des permissions administratives
 * Centralise la logique de permissions et fournit des helpers réutilisables
 */

import { useAuthStore } from '@/stores/auth';
import { handleApiError } from '@/utils/errorHandling';
import { computed } from 'vue';

export interface AdminPermissions {
  isAdmin: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canRead: boolean;
}

export interface AdminActions {
  handleApiError: (error: unknown, context?: string) => { message: string; type: string };
  checkPermission: (action: 'create' | 'edit' | 'delete' | 'read') => boolean;
  getPermissionMessage: (action: 'create' | 'edit' | 'delete' | 'read') => string;
}

/**
 * Composable principal pour les permissions admin
 */
export const useAdminPermissions = () => {
  const authStore = useAuthStore();

  // Permissions de base
  const permissions = computed((): AdminPermissions => {
    const isAdmin = authStore.user?.role === 'admin' || authStore.user?.role === 'superadmin';
    
    return {
      isAdmin,
      canCreate: isAdmin,
      canEdit: isAdmin,
      canDelete: isAdmin,
      canRead: true, // Lecture accessible à tous les utilisateurs authentifiés
    };
  });

  // Messages d'erreur pour les permissions
  const permissionMessages: Record<string, string> = {
    create: 'Seuls les administrateurs peuvent créer des éléments',
    edit: 'Seuls les administrateurs peuvent modifier des éléments',
    delete: 'Seuls les administrateurs peuvent supprimer des éléments',
    read: 'Vous n\'avez pas accès à cette ressource',
  };

  // Actions et helpers
  const actions: AdminActions = {
    /**
     * Gestionnaire d'erreur centralisé
     */
    handleApiError: (error: unknown, context?: string) => {
      return handleApiError(error, context);
    },

    /**
     * Vérifie si l'utilisateur a la permission pour une action
     */
    checkPermission: (action: 'create' | 'edit' | 'delete' | 'read'): boolean => {
      switch (action) {
        case 'create':
          return permissions.value.canCreate;
        case 'edit':
          return permissions.value.canEdit;
        case 'delete':
          return permissions.value.canDelete;
        case 'read':
          return permissions.value.canRead;
        default:
          return false;
      }
    },

    /**
     * Retourne le message d'erreur approprié pour une action
     */
    getPermissionMessage: (action: 'create' | 'edit' | 'delete' | 'read'): string => {
      return permissionMessages[action] || 'Action non autorisée';
    },
  };

  return {
    ...permissions.value,
    ...actions,
    permissions,
  };
};

/**
 * Composable spécialisé pour les boutons d'action
 * Fournit des propriétés prêtes à l'emploi pour les boutons
 */
export const useAdminButtons = () => {
  const { canCreate, canEdit, canDelete, getPermissionMessage } = useAdminPermissions();

  return {
    // Propriétés pour les boutons de création
    createButton: computed(() => ({
      disabled: !canCreate,
      title: canCreate ? 'Créer un nouvel élément' : getPermissionMessage('create'),
      class: canCreate ? '' : 'opacity-50 cursor-not-allowed',
    })),

    // Propriétés pour les boutons d'édition
    editButton: computed(() => ({
      disabled: !canEdit,
      title: canEdit ? 'Modifier cet élément' : getPermissionMessage('edit'),
      class: canEdit ? '' : 'opacity-50 cursor-not-allowed',
    })),

    // Propriétés pour les boutons de suppression
    deleteButton: computed(() => ({
      disabled: !canDelete,
      title: canDelete ? 'Supprimer cet élément' : getPermissionMessage('delete'),
      class: canDelete ? '' : 'opacity-50 cursor-not-allowed',
    })),

    // Permissions brutes
    canCreate,
    canEdit,
    canDelete,
  };
};

/**
 * Composable pour la gestion des erreurs dans les formulaires
 */
export const useAdminFormErrors = () => {
  const { handleApiError } = useAdminPermissions();

  /**
   * Traite une erreur de formulaire et retourne un objet structuré
   */
  const processFormError = (error: unknown, context?: string) => {
    const { message, type } = handleApiError(error, context);
    
    return {
      message,
      type,
      isPermissionError: type === 'permission',
      isValidationError: type === 'validation',
      isServerError: type === 'server',
      shouldRetry: type === 'server' || type === 'client',
      shouldReauth: type === 'permission' && message.includes('Session expirée'),
    };
  };

  return {
    processFormError,
    handleApiError,
  };
};

/**
 * Composable pour les notifications d'erreur
 */
export const useAdminNotifications = () => {
  const { handleApiError } = useAdminPermissions();

  /**
   * Détermine le type de notification selon l'erreur
   */
  const getNotificationType = (errorType: string): 'success' | 'error' | 'warning' | 'info' => {
    switch (errorType) {
      case 'permission':
        return 'warning';
      case 'validation':
        return 'error';
      case 'server':
        return 'error';
      default:
        return 'error';
    }
  };

  /**
   * Prépare une notification à partir d'une erreur
   */
  const prepareErrorNotification = (error: unknown, context?: string) => {
    const { message, type } = handleApiError(error, context);
    
    return {
      message,
      type: getNotificationType(type),
      duration: type === 'permission' ? 6000 : 5000, // Permissions restent plus longtemps
    };
  };

  return {
    prepareErrorNotification,
    getNotificationType,
  };
};

/**
 * Type helper pour les composants qui utilisent les permissions
 */
export type AdminPermissionsComposable = ReturnType<typeof useAdminPermissions>;
export type AdminButtonsComposable = ReturnType<typeof useAdminButtons>;
export type AdminFormErrorsComposable = ReturnType<typeof useAdminFormErrors>;
export type AdminNotificationsComposable = ReturnType<typeof useAdminNotifications>; 